import React from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, LucideIcon } from 'lucide-react';
import MetricTooltip from '@/components/system/MetricTooltip';

type KpiVariant = 'default' | 'success' | 'warning' | 'destructive';

interface ClickableKpiCardProps {
  label: string;
  value: string | number;
  icon: LucideIcon;
  variant?: KpiVariant;
  tooltip?: string;
  /** When provided, the card becomes clickable with visual affordance */
  onClick?: () => void;
  /** Short hint shown below the value (e.g. "Clique para detalhar") */
  actionHint?: string;
  className?: string;
}

const variantColor: Record<KpiVariant, string> = {
  default: 'text-foreground',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

const variantBg: Record<KpiVariant, string> = {
  default: 'bg-muted',
  success: 'bg-success/10',
  warning: 'bg-warning/10',
  destructive: 'bg-destructive/10',
};

/**
 * ClickableKpiCard — Componente oficial do Design System Netuno.
 * KPI card que pode ser passivo (sem onClick) ou interativo (com onClick).
 * Quando interativo, exibe indicadores visuais de navegabilidade:
 *   - cursor-pointer + hover ring
 *   - micro-elevação no hover
 *   - texto actionHint
 *   - ícone ChevronRight sutil
 */
const ClickableKpiCard: React.FC<ClickableKpiCardProps> = ({
  label,
  value,
  icon: Icon,
  variant = 'default',
  tooltip,
  onClick,
  actionHint,
  className,
}) => {
  const isClickable = !!onClick;

  return (
    <div
      className={cn(
        'emp-card p-4 flex items-center gap-3 transition-all duration-200',
        isClickable && 'cursor-pointer hover:ring-1 hover:ring-primary/20 hover:translate-y-[-1px] group',
        className,
      )}
      style={{ boxShadow: 'var(--shadow-card)' }}
      onClick={onClick}
      onMouseEnter={isClickable ? (e) => { e.currentTarget.style.boxShadow = 'var(--shadow-card-hover)'; } : undefined}
      onMouseLeave={isClickable ? (e) => { e.currentTarget.style.boxShadow = 'var(--shadow-card)'; } : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } } : undefined}
    >
      <div className={cn('p-2 rounded-lg flex-shrink-0', variantBg[variant])}>
        <Icon className={cn('h-5 w-5', variantColor[variant])} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-3xl font-bold tabular-nums leading-tight font-display', variantColor[variant])} style={{ letterSpacing: '-0.025em' }}>
          {value}
        </p>
        {tooltip ? (
          <MetricTooltip explanation={tooltip}>
            <p className="text-xs font-medium text-muted-foreground mt-1 truncate">{label}</p>
          </MetricTooltip>
        ) : (
          <p className="text-xs font-medium text-muted-foreground mt-1 truncate">{label}</p>
        )}
        {isClickable && actionHint && (
          <p className="text-xs text-muted-foreground/60 mt-1 flex items-center gap-0.5">
            <ChevronRight className="h-3 w-3" />
            {actionHint}
          </p>
        )}
      </div>
      {isClickable && (
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all flex-shrink-0" />
      )}
    </div>
  );
};

export { ClickableKpiCard };
export type { ClickableKpiCardProps };
export default ClickableKpiCard;
